import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {ChannelIconSizesEnum} from '../../../../../libs/shared/ui/src/components/icon/channelIcon';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellProps,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {CellButton} from '../../../../../libs/shared/ui/src/components/tables/helpers/cellButton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  findWarningsForChannel,
  getWarningProps,
} from '../../../../settings/inboxes/settingsChannelWarningHelpers';
import {makeGetChannelExtraProps} from '../../../../store/data/channels/extraChannelsSelectors';
import {getCompanyPreferences} from '../../../../store/data/company/companySelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {ChannelIconOrImage} from '../../channelIconOrImage';
import {OverflowWithCount} from '../../overflow/overflowWithCount';

/*
 * Props.
 */

interface ChannelsCellProps extends TableCellProps<ReadonlyArray<ChannelModel>> {
  onChannelClick: (channel: ChannelModel) => void;
  onOverflowClick: () => void;
}

/*
 * Style.
 */

const StyledCellButton = styled(CellButton)`
  margin-left: 4px;
  width: auto;

  &:first-child {
    margin-left: 0;
  }
`;

const StyledChannelButtonContent = styled.div`
  color: ${(p) => p.theme.interactiveText};
  margin-right: 6px;
  display: flex;
  gap: 7px;
  height: 100%;
`;

const StyledChannelIconOrImage = styled(ChannelIconOrImage)`
  margin-left: 3px;
`;

const StyledContentDiv = styled.div<TableCellStyleProps>`
  color: ${(p) => p.theme.greys.shade80};
  padding: 0 12px;
  font-size: ${fontSizes.medium};
  display: flex;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )};

  ${ellipsis()};
`;

const StyledOvercountCellButton = styled(CellButton)`
  ${center()};
  width: auto;
  font-weight: ${fontWeights.bold};
`;

const StyledChannelWarningUl = styled.ul`
  padding-left: 14px;
  margin: 0px;
`;

/*
 * Component.
 */

export const ChannelsCell: FC<ChannelsCellProps> = (props) => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  const renderSingleChannel = (channel: ChannelModel) => <SingleChannel channel={channel} {...props} />;

  const renderContent = () => {
    const channels = props.value || [];

    return (
      <StyledContentDiv
        $settings={settings}
        $isHovering={Boolean(isHovering)}
        $index={index}
        $isSelected={Boolean(isSelected)}
        $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
        $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      >
        <OverflowWithCount<ChannelModel>
          elements={channels}
          renderElement={renderSingleChannel}
          renderOverflowIndicator={(value) => (
            <StyledOvercountCellButton
              onClick={(e) => {
                e.preventDefault();
                props.onOverflowClick();
              }}
            >
              +{value}
            </StyledOvercountCellButton>
          )}
        />
      </StyledContentDiv>
    );
  };

  return (
    <TableCell<ReadonlyArray<ChannelModel>>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={renderContent}
    />
  );
};

const SingleChannel = (props: {channel: ChannelModel} & ChannelsCellProps) => {
  const {channel} = props;
  const getChannelExtraProps = useMemo(() => makeGetChannelExtraProps(() => channel), [channel]);
  const userNamespace = useAppSelector(getUserNamespace);
  const companyPreferences = useAppSelector(getCompanyPreferences);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const channelExtraProps = useAppSelector(getChannelExtraProps);
  const channelWarnings =
    (props.value && findWarningsForChannel(channel, userNamespace, companyPreferences)) || [];
  const channelWarningProps = channelWarnings.map(
    (warning) => [warning, getWarningProps(warning, isUserGlobalAdmin)] as const,
  );
  const theme = useUserTheme();

  const renderWarningTooltip = () => {
    if (channelWarningProps.length === 1) {
      const [, warningProps] = channelWarningProps[0];
      return warningProps.label;
    }
    return (
      <StyledChannelWarningUl>
        {channelWarningProps.map(([warning, warningProps]) => (
          <li key={warning}>{warningProps.label}</li>
        ))}
      </StyledChannelWarningUl>
    );
  };

  return (
    <StyledCellButton
      key={channel.id}
      onClick={(e) => {
        e.preventDefault();

        props.onChannelClick(channel);
      }}
      avatar={
        <StyledChannelIconOrImage
          imgSrc={channelExtraProps.imgSrc}
          type={channelExtraProps.type}
          size={ChannelIconSizesEnum.SMALL}
        />
      }
      avatarSizePx={16}
    >
      <StyledChannelButtonContent>
        {channel.displayName}
        {channelWarningProps.length > 0 && (
          <TooltipCoordinator isMultiline render={renderWarningTooltip}>
            <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} />
          </TooltipCoordinator>
        )}
      </StyledChannelButtonContent>
    </StyledCellButton>
  );
};
